import { createFileRoute } from '@tanstack/react-router'
import { Link } from '@tanstack/react-router'
import { Globe, Code, Smartphone, Bot, Check, ArrowRight } from 'lucide-react'
import { Button } from '../components/ui/button'

export const Route = createFileRoute('/pricing')({
  component: PricingPage,
})

interface PricingTier {
  name: string
  price: string
  description: string
  features: string[]
  highlighted?: boolean
  cta: string
}

interface ServicePricing {
  icon: React.ElementType
  title: string
  description: string
  color: string
  tiers: PricingTier[]
}

const servicePricing: ServicePricing[] = [
  {
    icon: Globe,
    title: "Website Development",
    description: "From landing pages to full-scale web applications",
    color: "from-blue-500 to-cyan-500",
    tiers: [
      {
        name: "Starter",
        price: "৳25,000",
        description: "Perfect for small businesses and startups",
        features: [
          "Up to 5 pages",
          "Responsive design",
          "Basic SEO setup",
          "Contact form",
          "1 month support",
          "Hosting setup assistance"
        ],
        cta: "Get Started"
      },
      {
        name: "Professional",
        price: "৳75,000",
        description: "For growing businesses with advanced needs",
        features: [
          "Up to 15 pages",
          "Custom design",
          "Advanced SEO optimization",
          "CMS integration",
          "E-commerce ready (up to 50 products)",
          "3 months support",
          "Performance optimization",
          "Analytics integration"
        ],
        highlighted: true,
        cta: "Most Popular"
      },
      {
        name: "Enterprise",
        price: "Custom",
        description: "Tailored solutions for large organizations",
        features: [
          "Unlimited pages",
          "Custom functionality",
          "Multi-language support",
          "Advanced integrations",
          "Priority support",
          "Dedicated account manager",
          "SLA guarantee"
        ],
        cta: "Contact Us"
      }
    ]
  },
  {
    icon: Code,
    title: "Software Development",
    description: "Custom software solutions for your business",
    color: "from-indigo-500 to-purple-500",
    tiers: [
      {
        name: "Starter",
        price: "৳150,000",
        description: "Simple business applications",
        features: [
          "Single module application",
          "Basic database design",
          "User authentication",
          "Admin dashboard",
          "2 months development",
          "1 month support"
        ],
        cta: "Get Started"
      },
      {
        name: "Professional",
        price: "৳400,000",
        description: "Comprehensive business solutions",
        features: [
          "Multi-module system",
          "Complex database architecture",
          "Role-based access control",
          "API development",
          "Third-party integrations",
          "4 months development",
          "3 months support",
          "Documentation included"
        ],
        highlighted: true,
        cta: "Most Popular"
      },
      {
        name: "Enterprise",
        price: "Custom",
        description: "Large-scale enterprise systems",
        features: [
          "Full ERP/CRM systems",
          "Microservices architecture",
          "Cloud infrastructure setup",
          "DevOps & CI/CD pipeline",
          "Scalability planning",
          "Ongoing maintenance",
          "24/7 support option"
        ],
        cta: "Contact Us"
      }
    ]
  },
  {
    icon: Smartphone,
    title: "Mobile App Development",
    description: "iOS and Android applications",
    color: "from-pink-500 to-rose-500",
    tiers: [
      {
        name: "Starter",
        price: "৳100,000",
        description: "Simple mobile applications",
        features: [
          "Single platform (iOS or Android)",
          "Up to 10 screens",
          "Basic features",
          "Push notifications",
          "App Store submission",
          "1 month support"
        ],
        cta: "Get Started"
      },
      {
        name: "Professional",
        price: "৳200,000",
        description: "Cross-platform applications",
        features: [
          "iOS & Android (React Native)",
          "Up to 25 screens",
          "Backend integration",
          "User authentication",
          "Offline functionality",
          "In-app purchases ready",
          "3 months support",
          "Performance optimization"
        ],
        highlighted: true,
        cta: "Most Popular"
      },
      {
        name: "Enterprise",
        price: "Custom",
        description: "Complex mobile solutions",
        features: [
          "Native development option",
          "Unlimited screens",
          "Complex features",
          "Custom animations",
          "Advanced security",
          "Dedicated team",
          "Long-term partnership"
        ],
        cta: "Contact Us"
      }
    ]
  },
  {
    icon: Bot,
    title: "AI and API Integration",
    description: "Intelligent automation and integrations",
    color: "from-amber-500 to-orange-500",
    tiers: [
      {
        name: "Starter",
        price: "৳50,000",
        description: "Basic AI features and integrations",
        features: [
          "Single AI feature integration",
          "ChatGPT/Claude API setup",
          "Basic chatbot",
          "Up to 3 API integrations",
          "Documentation",
          "1 month support"
        ],
        cta: "Get Started"
      },
      {
        name: "Professional",
        price: "৳150,000",
        description: "Advanced AI solutions",
        features: [
          "Multiple AI features",
          "Custom prompt engineering",
          "Advanced chatbot with context",
          "Workflow automation",
          "Up to 10 API integrations",
          "Data pipeline setup",
          "3 months support"
        ],
        highlighted: true,
        cta: "Most Popular"
      },
      {
        name: "Enterprise",
        price: "Custom",
        description: "Full AI transformation",
        features: [
          "Custom AI model training",
          "Complex automation workflows",
          "Real-time data processing",
          "Enterprise integrations",
          "AI strategy consulting",
          "Dedicated AI engineer",
          "Ongoing optimization"
        ],
        cta: "Contact Us"
      }
    ]
  }
]

function PricingPage() {
  return (
    <>
      {/* Hero Section */}
      <section className="relative z-10 pt-32 pb-16 container mx-auto px-6">
        <div className="max-w-4xl mx-auto text-center space-y-6">
          <p className="text-sm font-medium text-indigo-400">Pricing</p>
          <h1 className="text-4xl md:text-6xl font-bold">
            Transparent Pricing for Bangladesh
          </h1>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto">
            Quality digital solutions at competitive prices. All prices in BDT (৳).
            Custom quotes available for complex requirements.
          </p>
        </div>
      </section>

      {/* Pricing Sections */}
      {servicePricing.map((service, serviceIndex) => {
        const Icon = service.icon
        return (
          <section key={serviceIndex} className="relative z-10 py-16 container mx-auto px-6">
            <div className="max-w-6xl mx-auto">
              {/* Service Header */}
              <div className="flex items-center gap-4 mb-12">
                <div className={`flex h-12 w-12 items-center justify-center rounded-xl bg-linear-to-br ${service.color}`}>
                  <Icon className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h2 className="text-2xl md:text-3xl font-bold text-white">{service.title}</h2>
                  <p className="text-slate-400">{service.description}</p>
                </div>
              </div>

              {/* Pricing Cards */}
              <div className="grid md:grid-cols-3 gap-6">
                {service.tiers.map((tier, tierIndex) => (
                  <div
                    key={tierIndex}
                    className={`relative rounded-3xl p-8 transition-all duration-300 ${tier.highlighted
                      ? 'bg-linear-to-b from-indigo-500/20 to-transparent border-2 border-indigo-500/50 scale-105'
                      : 'bg-slate-900/30 border border-white/5 hover:border-white/10'
                      }`}
                  >
                    {tier.highlighted && (
                      <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-indigo-500 text-white text-xs font-semibold px-4 py-1 rounded-full">
                        RECOMMENDED
                      </div>
                    )}

                    <div className="mb-6">
                      <h3 className="text-xl font-semibold text-white mb-2">{tier.name}</h3>
                      <div className="flex items-baseline gap-1 mb-2">
                        <span className="text-4xl font-bold text-white">{tier.price}</span>
                        {tier.price !== "Custom" && <span className="text-slate-400 text-sm">BDT</span>}
                      </div>
                      <p className="text-slate-400 text-sm">{tier.description}</p>
                    </div>

                    <ul className="space-y-3 mb-8">
                      {tier.features.map((feature, featureIndex) => (
                        <li key={featureIndex} className="flex items-start gap-3">
                          <Check className="h-5 w-5 text-indigo-400 shrink-0 mt-0.5" />
                          <span className="text-slate-300 text-sm">{feature}</span>
                        </li>
                      ))}
                    </ul>

                    <Button
                      asChild
                      className={`w-full ${tier.highlighted
                        ? 'bg-indigo-600 hover:bg-indigo-700'
                        : 'bg-white/5 hover:bg-white/10 border border-white/10'
                        }`}
                    >
                      <Link to="/contact" className="flex items-center justify-center gap-2">
                        {tier.cta}
                        <ArrowRight className="h-4 w-4" />
                      </Link>
                    </Button>
                  </div>
                ))}
              </div>
            </div>

            {/* Divider (except last) */}
            {serviceIndex < servicePricing.length - 1 && (
              <div className="max-w-6xl mx-auto mt-16 border-t border-white/5" />
            )}
          </section>
        )
      })}

      {/* FAQ Section */}
      <section className="relative z-10 py-24 container mx-auto px-6">
        <div className="max-w-3xl mx-auto text-center bg-slate-900/30 border border-white/5 rounded-3xl p-12">
          <h2 className="text-3xl font-bold mb-4">Have Questions?</h2>
          <p className="text-slate-400 mb-8">
            Not sure which plan is right for you? Let's discuss your project requirements
            and find the perfect solution within your budget.
          </p>
          <Button asChild size="lg" className="bg-indigo-600 hover:bg-indigo-700">
            <Link to="/contact">Get a Custom Quote</Link>
          </Button>
        </div>
      </section>
    </>
  )
}
